import { humanFileSize } from '@mi-front/core';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { UploadState } from '../../types';

interface Props {
  readonly index: number;
  readonly upload: UploadState;
  readonly remove: () => void;
  readonly updateId: (value: string) => void;
}

export function UploadItem(p: Props) {
  const { index, upload, remove } = p;
  const { $t } = useIntl();

  const getBadgeColor = (status: UploadState['status']) => {
    switch (status) {
      case 'NOT_STARTED':
        return 'default';
      case 'STARTED':
        return 'info';
      case 'COMPLETED':
        return 'success';
      case 'ERROR':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card data-cy="upload-item">
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography
                data-cy="item-index"
                variant="body2"
                sx={{
                  width: '4rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                #{index + 1}
              </Typography>
              <Box>
                <Typography data-cy="item-name" variant="body1">
                  {upload.file.name}
                </Typography>
                <Typography data-cy="item-size" variant="caption" color="text.secondary">
                  {humanFileSize(upload.file.size)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              disabled={upload.status !== 'NOT_STARTED'}
              label={$t({
                id: 'audit.filesUpload.audioIdLabel',
                defaultMessage: 'ID material',
              })}
              size="small"
              sx={{ width: 300 }}
              value={p.upload.audioId}
              onChange={(evt) => p.updateId(evt.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            {upload.status === 'NOT_STARTED' && (
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={remove}
                  startIcon={<DeleteIcon />}
                >
                  {$t({
                    id: 'audit.uploadScreen.item.remove',
                    defaultMessage: 'Eliminar',
                  })}
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
        data-cy="item-status"
      >
        <Typography variant="body2" component="span">
          <FormattedMessage id="audit.uploadScreen.item.status" defaultMessage="Status:" />
        </Typography>
        {upload.status === 'NOT_STARTED' && (
          <Chip
            size="small"
            label={
              <FormattedMessage
                id="audit.uploadScreen.item.notStarted"
                defaultMessage="No cargado"
              />
            }
            color={getBadgeColor(upload.status)}
            variant="outlined"
          />
        )}
        {upload.status === 'STARTED' && (
          <>
            <Chip
              size="small"
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <CircularProgress size={12} />
                  <FormattedMessage
                    id="audit.uploadScreen.item.started"
                    defaultMessage="Cargando"
                  />
                </Box>
              }
              color={getBadgeColor(upload.status)}
            />
            <Box sx={{ flexGrow: 1, ml: 2 }}>
              <LinearProgress
                variant="determinate"
                value={upload.progress}
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {upload.progress}%
              </Typography>
            </Box>
          </>
        )}
        {upload.status === 'COMPLETED' && (
          <Chip
            size="small"
            label={
              <>
                <FormattedMessage
                  id="audit.uploadScreen.item.completed"
                  defaultMessage="Carga realizada con éxito"
                />
                {upload.overridden && (
                  <>
                    {' '}
                    <FormattedMessage
                      id="audit.uploadScreen.item.overridden"
                      defaultMessage="(la versión anterior del archivo fue actualizada)"
                    />
                  </>
                )}
              </>
            }
            color={getBadgeColor(upload.status)}
          />
        )}
        {upload.status === 'ERROR' && (
          <Chip
            size="small"
            label={
              <>
                <FormattedMessage
                  id="audit.uploadScreen.item.error"
                  defaultMessage="Error: {message}"
                  values={{
                    message: upload.error.message,
                  }}
                />
                {upload.error.code && (
                  <>
                    {' '}
                    <FormattedMessage
                      id="audit.uploadScreen.item.code"
                      defaultMessage="(Código de error: {code})"
                      values={{
                        code: upload.error.code,
                      }}
                    />
                  </>
                )}
              </>
            }
            color={getBadgeColor(upload.status)}
          />
        )}
      </Box>
    </Card>
  );
}
